import React, { useCallback, useEffect, useState } from "react";

import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Skeleton,
  Card,
  CardContent,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

import AppButton from "../ui/AppButton";
import { getEmployees } from "../services/employeeService";
import { getProjects } from "../services/projectService";
import CapacityCalendar from "../components/projects/CapacityCalendar";
import AssignProjectDialog from "../components/projects/AssignProjectDialog";
import { useUserRole } from "../context/UserRoleContext";
import { USER_ROLES, canAccessPage, canWritePage } from "../auth/roles";

function ResourceManagement() {
  const { role, employee } = useUserRole();
  const canManageResources = canWritePage(role, "resource_management");

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [search, setSearch] = useState("");
  const [employeeProjects, setEmployeeProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setLoading(true);

        // Read-only roles (e.g. Employee/Member) can only view their own capacity.
        if (!canManageResources) {
          if (employee) {
            setEmployees([employee]);
            setSelectedEmployeeId(employee.employee_id);
          }
          return;
        }

        let managerId = null;
        if (role === USER_ROLES.MANAGER) {
          managerId = employee?.employee_id ?? -1;
        }

        const data = await getEmployees(search, managerId, null);
        setEmployees(data);

        if (data.length > 0 && !selectedEmployeeId) {
          setSelectedEmployeeId(data[0].employee_id);
        }
      } catch (error) {
        console.error("Error loading employees:", error);
      } finally {
        setLoading(false);
      }
    };

    loadEmployees();
  }, [search, role, employee, selectedEmployeeId, canManageResources]);

  const loadEmployeeProjects = useCallback(async () => {
    if (!selectedEmployeeId) {
      setEmployeeProjects([]);
      return;
    }

    try {
      setLoadingProjects(true);
      const data = await getProjects("", selectedEmployeeId);
      setEmployeeProjects(data);
    } catch (error) {
      console.error("Error loading employee projects:", error);
    } finally {
      setLoadingProjects(false);
    }
  }, [selectedEmployeeId]);

  useEffect(() => {
    loadEmployeeProjects();
  }, [loadEmployeeProjects]);

  const handleProjectAssigned = () => {
    loadEmployeeProjects();
    setRefreshKey((prev) => prev + 1);
  };

  if (loading && employees.length === 0) {
    return (
      <Box
        sx={{
          bgcolor: "background.default",
          minHeight: "100vh",
          p: 3
        }}
      >
        <Skeleton variant="text" width={260} height={40} />
        <Skeleton variant="text" width={340} height={22} sx={{ mb: 3 }} />

        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <Skeleton variant="rounded" height={40} sx={{ flex: 1, minWidth: 250, borderRadius: 2 }} />
          <Skeleton variant="rounded" height={40} sx={{ minWidth: 250, borderRadius: 2 }} />
        </Box>

        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Skeleton variant="text" width={160} height={28} />
                <Skeleton variant="text" width={120} height={20} />
              </Box>

              <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 999 }} />
            </Box>

            <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2 }} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  const isReadOnlyViewer = !canManageResources;

  if (!canAccessPage(role, "resource_management")) {
    return (
      <Box
        sx={{
          bgcolor: "background.default",
          minHeight: "100vh",
          p: 3
        }}
      >
        <Alert severity="info">
          Only Managers and Admins can access resource management
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        p: 3
      }}
    >
      <Box mb={3}>
        <Typography variant="h4" fontWeight={600} color="text.primary" mb={0.5}>
          Resource Management
        </Typography>

        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          View and manage employee capacity allocation across projects
        </Typography>
      </Box>

      {!isReadOnlyViewer && (
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <Box sx={{ flex: 1, minWidth: 250 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search employees"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "#94a3b8" }} />
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "background.paper"
                }
              }}
            />
          </Box>

          {employees.length > 0 && (
            <Box sx={{ minWidth: 250 }}>
              <TextField
                select
                fullWidth
                size="small"
                value={selectedEmployeeId || ""}
                onChange={(e) => setSelectedEmployeeId(parseInt(e.target.value))}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "background.paper"
                  }
                }}
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.employee_id} value={emp.employee_id}>
                    {emp.employee_name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </Box>
      )}

      {selectedEmployeeId && (
        <Box>
          {employees
            .filter((emp) => emp.employee_id === selectedEmployeeId)
            .map((emp) => (
              <Card key={emp.employee_id} sx={{ borderRadius: 3, mb: 3 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {emp.employee_name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {emp.department_name || "No Department"}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={1} alignItems="center">
                      {!isReadOnlyViewer && (
                        <AppButton
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => setAssignDialogOpen(true)}
                          sx={{ height: "auto" }}
                        >
                          Assign Project
                        </AppButton>
                      )}
                    </Box>
                  </Box>

                  <CapacityCalendar employeeId={emp.employee_id} refreshKey={refreshKey} />

                  <Box mt={3}>
                    <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
                      Assigned Projects
                    </Typography>

                    <TableContainer
                      component={Paper}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: "none"
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: "background.default" }}>
                            {[
                              "Project Name",
                              "Description",
                              "Capacity %",
                              "Start Date",
                              "End Date",
                              "Team"
                            ].map((head) => (
                              <TableCell
                                key={head}
                                sx={{ fontWeight: 600, fontSize: 12, color: "#475569" }}
                              >
                                {head}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {loadingProjects && (
                            <TableRow>
                              <TableCell colSpan={6}>
                                <Skeleton variant="text" height={24} />
                              </TableCell>
                            </TableRow>
                          )}

                          {!loadingProjects && employeeProjects.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={6}>
                                <Typography fontSize={13} color="textSecondary" sx={{ py: 1 }}>
                                  No projects assigned
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}

                          {!loadingProjects && employeeProjects.map((project) => (
                            <TableRow key={project.project_id}>
                              <TableCell>
                                <Typography fontSize={13} fontWeight={600}>
                                  {project.project_name}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Typography
                                  fontSize={13}
                                  color="text.secondary"
                                  sx={{
                                    maxWidth: 260,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                  }}
                                >
                                  {project.description || "-"}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Chip
                                  label={`${project.capacity_percent}%`}
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                    background: "#eff6ff",
                                    color: "#2563eb"
                                  }}
                                />
                              </TableCell>

                              <TableCell>
                                <Typography fontSize={13} color="text.secondary">
                                  {project.start_date || "-"}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Typography fontSize={13} color="text.secondary">
                                  {project.end_date || "-"}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {(project.assigned_names || [])
                                    .filter((name) => name !== emp.employee_name)
                                    .map((name) => (
                                      <Chip
                                        key={name}
                                        label={name}
                                        size="small"
                                        sx={{
                                          borderRadius: 2,
                                          background: "#e0f2fe",
                                          color: "#0369a1",
                                          fontWeight: 500
                                        }}
                                      />
                                    ))}
                                  {(project.assigned_names || []).filter((name) => name !== emp.employee_name).length === 0 && (
                                    <Typography fontSize={12} color="textSecondary">-</Typography>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </CardContent>
              </Card>
            ))}
        </Box>
      )}

      {employees.length === 0 && (
        <Card sx={{ borderRadius: 3, textAlign: "center", p: 4 }}>
          <Typography color="textSecondary">
            No employees found
          </Typography>
        </Card>
      )}

      <AssignProjectDialog
        open={assignDialogOpen}
        employee={employees.find((emp) => emp.employee_id === selectedEmployeeId) || null}
        onClose={() => setAssignDialogOpen(false)}
        onAssigned={handleProjectAssigned}
      />
    </Box>
  );
}

export default ResourceManagement;
